import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, Plus, History, TicketPercent } from 'lucide-react';
import { fetchCupones, Cupon } from '../../../services/couponService';
import AddModalCupon from '../../../components/Coupon/AddModalCupon';
import EditModalCupon from '../../../components/Coupon/EditModalCupon';
import DeleteModalCupon from '../../../components/Coupon/DeleteModalCupon';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';
import { useNavigate } from 'react-router-dom';

const ITEMS_PER_PAGE = 10;

const CouponPage: React.FC = () => {
    const [cupones, setCupones] = useState<Cupon[]>([]);
    const [currentPage, setCurrentPage] = useState(1);

    const [mostrarModalAgregar, setMostrarModalAgregar] = useState(false);
    const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
    const [cuponEditar, setCuponEditar] = useState<Cupon | null>(null);

    const [mostrarModalEliminar, setMostrarModalEliminar] = useState(false);
    const [cuponEliminar, setCuponEliminar] = useState<Cupon | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('cupon', userLevelId);

    const cargarCupones = useCallback(async () => {
        try {
            if (perm?.can_read) {
                const data = await fetchCupones();
                const ordenados = data.sort((a, b) => 
                    new Date(b.fecha_creacion).getTime() - new Date(a.fecha_creacion).getTime()
                );
                setCupones(ordenados);
            } else {
                setCupones([]);
            }
        } catch (error) {
            /* Browser diagnostics omitted. */
            setCupones([]);
        }
    }, [perm]);

    useEffect(() => {
        cargarCupones();
    }, [cargarCupones]);

    // Paginación
    const totalPages = Math.ceil(cupones.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const cuponesMostrados = cupones.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const abrirModalEditar = (cupon: Cupon) => {
        setCuponEditar(cupon);
        setMostrarModalEditar(true);
    };

    const abrirModalEliminar = (cupon: Cupon) => {
        setCuponEliminar(cupon);
        setMostrarModalEliminar(true);
    };

    if (perm === null) return <Loading text="Cargando cupones..." />;

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Cupones</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/coupon" className="text-[#c1348b] hover:text-[#8f1f64]">Cupones</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    {/* Botón de historial (solo si tiene permiso de lectura) */}
                    {perm.can_read && (
                        <button
                            className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                            onClick={() => navigate('/coupon/history')} // Ajusta la ruta según tu aplicación
                        >
                            <History className="w-5 h-5" />
                        </button>
                    )}

                    {perm.can_create && (
                        <button
                            onClick={() => setMostrarModalAgregar(true)}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Cupón
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Modales */}
                {mostrarModalAgregar && (
                    <AddModalCupon
                        onClose={() => setMostrarModalAgregar(false)}
                        onSuccess={() => {
                            setMostrarModalAgregar(false);
                            cargarCupones();
                        }}
                    />
                )}
                {mostrarModalEditar && cuponEditar && (
                    <EditModalCupon
                        cupon={cuponEditar}
                        onClose={() => {
                            setMostrarModalEditar(false);
                            setCuponEditar(null);
                        }}
                        onSuccess={() => {
                            setMostrarModalEditar(false);
                            setCuponEditar(null);
                            cargarCupones();
                        }}
                    />
                )}
                <DeleteModalCupon
                    isOpen={mostrarModalEliminar}
                    cupon={cuponEliminar}
                    onClose={() => {
                        setMostrarModalEliminar(false);
                        setCuponEliminar(null);
                    }}
                    onDeleted={() => {
                        setMostrarModalEliminar(false);
                        setCuponEliminar(null);
                        cargarCupones();
                    }}
                />

                {/* Modal Sesión Expirada */}
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {/* Tabla */}
                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descuento Monto</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descuento %</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan y Servicio</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Inicio</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Fin</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Uso Único</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {perm.can_read && cuponesMostrados.map(cupon => (
                                <tr key={cupon.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.codigo}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.descripcion}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.descuento_monto ?? '—'} Bs.</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.descuento_porcentaje ?? '—'}%</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        {cupon.plan_aplicable
                                            ? `${cupon.plan_aplicable.plan_nombre} - ${cupon.plan_aplicable.servicio_nombre}`
                                            : 'Todos'}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{new Date(cupon.fecha_inicio).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{new Date(cupon.fecha_fin).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{new Date(cupon.fecha_creacion).toLocaleString()}</td>
                                    <td className="px-4 py-4 text-sm">
                                        {cupon.uso_unico_por_cliente ? (
                                            <span className="px-2 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800">Sí</span>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800">No</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="flex gap-2">
                                            {perm.can_update && (
                                                <button
                                                    onClick={() => abrirModalEditar(cupon)}
                                                    className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                            {perm.can_delete && (
                                                <button
                                                    onClick={() => abrirModalEliminar(cupon)}
                                                    className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {/* Mensaje cuando no hay datos o no tiene permiso de lectura */}
                            {(!perm.can_read || cupones.length === 0) && (
                                <tr>
                                    <td colSpan={10} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <TicketPercent className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                {!perm.can_read 
                                                    ? 'No tienes permiso para ver cupones' 
                                                    : 'No hay cupones disponibles'}
                                            </span>
                                            {perm.can_create && perm.can_read && (
                                                <p className="text-gray-500 text-sm">
                                                    Agrega un nuevo cupón para comenzar
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {/* Paginación (solo si hay datos y permiso de lectura) */}
                {perm.can_read && cupones.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={ITEMS_PER_PAGE}
                        totalItems={cupones.length}
                    />
                )}
            </div>
        </div>
    );
};

export default CouponPage;
